/**
 * <ma-cart-drawer> — Mar Amoli mini-cart Web Component.
 * - Opens via global event `ma:cart:open` or buttons with [data-ma-cart-open]
 * - Closes via overlay click, [data-ma-drawer-close], Escape key, or `ma:cart:close` event
 * - Updates free-shipping progress and totals using safe DOM (textContent / createElement)
 *
 * Security: never assigns innerHTML on user-influenced data. Item refresh uses
 * server-rendered snippet via Section Rendering API (future).
 */
const FOCUSABLE_SEL = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class MaCartDrawer extends HTMLElement {
  connectedCallback() {
    this._onKey = this._onKey.bind(this);
    this._onGlobalOpen = this._onGlobalOpen.bind(this);
    this._onGlobalClose = this._onGlobalClose.bind(this);
    this._onLineClick = this._onLineClick.bind(this);
    this._onLineChange = this._onLineChange.bind(this);
    this._lastFocus = null;
    this._busy = false;
    // Lo que el servidor ya tiene guardado. Sin esto, el primer clic en "Ir a
    // pagar" con la nota vacía dispararía un POST inútil y retrasaría el
    // checkout.
    this._syncNota();

    // Cierre + edición de líneas (stepper / quitar) por event delegation en
    // el drawer, para que sobrevivan al re-render del contenido tras cada
    // cambio del carrito.
    this.addEventListener('click', this._onLineClick);
    this.addEventListener('change', this._onLineChange);

    // Hint de scroll: capture porque el evento scroll no burbujea; así
    // sobrevive al re-render del cuerpo del drawer.
    this._onScroll = () => this._updateScrollHint();
    this.addEventListener('scroll', this._onScroll, true);

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-ma-cart-open]')) {
        e.preventDefault();
        this.open();
      }
    });

    document.addEventListener('ma:cart:open', this._onGlobalOpen);
    document.addEventListener('ma:cart:close', this._onGlobalClose);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('ma:cart:open', this._onGlobalOpen);
    document.removeEventListener('ma:cart:close', this._onGlobalClose);
    document.removeEventListener('keydown', this._onKey);
  }

  _onGlobalOpen() { this.open(); }
  _onGlobalClose() { this.close(); }
  _onKey(e) {
    if (!this.hasAttribute('open')) return;
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    // Focus trap: Tab cycles dentro del drawer
    if (e.key === 'Tab') {
      const focusables = this._getFocusables();
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  _getFocusables() {
    return Array.from(this.querySelectorAll(FOCUSABLE_SEL)).filter((el) => {
      // Filtra elementos hidden o display: none
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  // ── Edición de líneas del carrito (stepper + quitar) ──────────────────
  _onLineClick(e) {
    if (e.target.closest('[data-ma-drawer-close]')) { this.close(); return; }
    const dec = e.target.closest('[data-ma-qty-decrement]');
    if (dec) { e.preventDefault(); this._stepLine(dec, -1); return; }
    const inc = e.target.closest('[data-ma-qty-increment]');
    if (inc) { e.preventDefault(); this._stepLine(inc, 1); return; }
    const rem = e.target.closest('[data-ma-cart-remove]');
    if (rem) { e.preventDefault(); this._removeLine(rem); return; }

    // Ir a pagar sin haber salido del campo de la nota: `change` no alcanzó a
    // dispararse y la nota se perdería. Detenemos la navegación lo que tarde
    // el POST y seguimos aunque falle — quedarse atorado en el drawer por una
    // nota es peor que perder la nota.
    const pagar = e.target.closest('[data-ma-checkout]');
    if (pagar) {
      const campo = this.querySelector('[data-ma-cart-note]');
      if (campo && campo.value !== this._notaGuardada) {
        e.preventDefault();
        const destino = pagar.getAttribute('href') || '/checkout';
        this._saveNote().then(() => { window.location.href = destino; });
      }
    }
  }

  _onLineChange(e) {
    // `change` en un textarea dispara al perder el foco, que es justo cuando
    // queremos guardar: ni en cada tecla ni sólo al final.
    if (e.target.closest('[data-ma-cart-note]')) {
      this._saveNote();
      return;
    }
    const input = e.target.closest('[data-ma-qty-input]');
    if (!input) return;
    const li = input.closest('[data-key]');
    if (!li) return;
    let qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    this._updateLine(li.getAttribute('data-key'), qty);
  }

  _stepLine(btn, delta) {
    const li = btn.closest('[data-key]');
    if (!li) return;
    const input = li.querySelector('[data-ma-qty-input]');
    const current = (input && parseInt(input.value, 10)) || 1;
    // Mínimo 1 desde el stepper; para vaciar la línea está el botón Quitar.
    const next = Math.max(1, current + delta);
    if (next === current) return;
    this._updateLine(li.getAttribute('data-key'), next);
  }

  _removeLine(btn) {
    const li = btn.closest('[data-key]');
    if (!li) return;
    this._updateLine(li.getAttribute('data-key'), 0);
  }

  async _updateLine(key, quantity) {
    if (!key || this._busy) return;
    this._busy = true;
    this.setAttribute('data-updating', '');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: 'cart-drawer',
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error('cart change failed: ' + res.status);
      const cart = await res.json();
      if (cart.sections && cart.sections['cart-drawer']) {
        this._renderSection(cart.sections['cart-drawer'], cart.item_count);
      }
      // El footer/subtotal/envío ya vienen en el HTML re-renderizado; sólo
      // sincronizamos los badges de conteo de fuera del drawer y avisamos.
      this.updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('ma:cart:changed'));
    } catch (err) {
      console.error('Cart line update failed', err);
    } finally {
      this._busy = false;
      this.removeAttribute('data-updating');
    }
  }

  /**
   * Guarda la nota del pedido en el carrito de Shopify.
   *
   * No usa `sections` en la respuesta a propósito: re-renderizar el drawer
   * aquí reemplazaría el textarea que la persona quizá siga editando.
   *
   * Devuelve una promesa para que el checkout pueda esperarla.
   */
  // Toma como "ya guardado" lo que venga renderizado del servidor. Se llama al
  // conectar y después de cada re-render que pudo reemplazar el campo.
  _syncNota() {
    const campo = this.querySelector('[data-ma-cart-note]');
    this._notaGuardada = campo ? campo.value : '';
  }

  _saveNote() {
    const campo = this.querySelector('[data-ma-cart-note]');
    if (!campo) return Promise.resolve();

    const texto = campo.value;
    // Nada que hacer si no cambió desde el último guardado. Evita un POST por
    // cada blur del campo cuando la persona sólo lo abrió y lo cerró.
    if (texto === this._notaGuardada) return Promise.resolve();

    this._notaGuardada = texto;
    const estado = this.querySelector('[data-ma-cart-note-estado]');

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: texto }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('cart note update failed: ' + res.status);
        if (estado) estado.textContent = texto ? 'Nota guardada' : '';
      })
      .catch((err) => {
        console.error('Cart note update failed', err);
        // Se reintenta al siguiente blur: sin esto la nota se daría por
        // guardada aunque el POST hubiera fallado.
        this._notaGuardada = undefined;
        if (estado) estado.textContent = 'No se pudo guardar la nota.';
      });
  }

  _renderSection(html, itemCount) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('ma-cart-drawer');
    if (!fresh) return;
    // Reemplaza SOLO las zonas que cambian al editar líneas, dejando intactas
    // las recomendaciones (rec-pane desktop + carousel mobile) que ya están
    // pobladas por component-cart-recommendations.js — cambiar la cantidad no
    // debe resetear "Te puede gustar" a su esqueleto.
    const curItems = this.querySelector('.ma-cart__items');
    if (itemCount > 0 && curItems) {
      this._swap(fresh, '.ma-cart__items');     // ya había items: solo la lista
    } else {
      this._swap(fresh, '[data-ma-cart-body]'); // vacío, o transición vacío→con items
    }
    this._swap(fresh, '[data-ma-cart-shipping-wrap]');
    this._swap(fresh, '[data-ma-cart-foot]');
    this._swap(fresh, '.ma-drawer__title-count');
    // Si el cuerpo se reemplazó completo (vacío ⇄ con items), el textarea de la
    // nota es otro nodo: hay que volver a leer qué tiene guardado el servidor.
    this._syncNota();
    this._updateScrollHint();
  }

  // Muestra el hint "Desplázate para ver más artículos" solo cuando la
  // lista tiene scroll y estás arriba; al bajar se oculta.
  _updateScrollHint() {
    const body = this.querySelector('[data-ma-cart-body]');
    const hint = this.querySelector('[data-ma-scroll-hint]');
    if (!body || !hint) return;
    const overflow = body.scrollHeight > body.clientHeight + 8;
    const atTop = body.scrollTop < 24;
    hint.hidden = !(overflow && atTop);
  }

  _swap(freshRoot, selector) {
    const fresca = freshRoot.querySelector(selector);
    const actual = this.querySelector(selector);
    if (fresca && actual) actual.replaceWith(fresca);
  }

  open() {
    // «Atrás» cierra el carrito (2-nov-2026). El cajón no es una página, así
    // que el botón atrás del celular se saltaba la ficha: «si me regreso, me
    // regresa a la página principal» (encuesta Zigpoll, 28-oct). Se deja una
    // entrada en el historial al abrir; `popstate` la consume y cierra.
    if (!this.hasAttribute('open') && !(history.state && history.state.maCarrito)) {
      try { history.pushState({ maCarrito: 1 }, ''); } catch (e) {}
    }
    this._lastFocus = document.activeElement;
    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    // Focus en el botón de cierre (no en el primer elemento — ese suele ser
    // un link de checkout y un screen reader anunciaría "Ir al checkout"
    // como primer elemento, confuso). Cierre primero da contexto.
    const closeBtn = this.querySelector('[data-ma-drawer-close]');
    if (closeBtn) closeBtn.focus();
    // Notifica a otros módulos que el drawer se abrió.
    document.dispatchEvent(new CustomEvent('ma:cart:opened', { bubbles: true }));
    // rAF + fallback tras la transición de apertura: en el primer frame el
    // cuerpo aún no tiene su alto final y el hint saldría oculto.
    requestAnimationFrame(() => this._updateScrollHint());
    setTimeout(() => this._updateScrollHint(), 360);
  }

  close(desdeHistorial) {
    // Si lo cerró un botón (no el «atrás»), se consume la entrada que dejó
    // `open()`, o el siguiente «atrás» no haría nada visible.
    if (!desdeHistorial && history.state && history.state.maCarrito) {
      try { history.back(); } catch (e) {}
    }
    this.removeAttribute('open');
    document.documentElement.style.overflow = '';
    setTimeout(() => {
      if (!this.hasAttribute('open')) this.setAttribute('hidden', '');
      // Restaurar focus al elemento que abrió el drawer
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
        try { this._lastFocus.focus(); } catch (e) {}
      }
    }, 350);
  }

  /**
   * Update the free-shipping message and progress fill.
   * @param {number} subtotalCents — cart total in cents
   */
  updateShipping(subtotalCents) {
    const threshold = parseInt(this.dataset.freeShippingThreshold, 10) * 100;
    const msgEl = this.querySelector('[data-ma-cart-shipping]');
    const fillEl = this.querySelector('[data-ma-cart-shipping-fill]');
    const costEl = this.querySelector('[data-ma-cart-shipping-cost]');
    if (!msgEl) return;

    // Clear existing content
    while (msgEl.firstChild) msgEl.removeChild(msgEl.firstChild);

    if (subtotalCents >= threshold) {
      msgEl.classList.add('is-unlocked');
      const strong = document.createElement('strong');
      strong.textContent = '🎉 ¡Tienes envío gratis!';
      msgEl.appendChild(strong);
      if (fillEl) fillEl.style.width = '100%';
      if (costEl) costEl.textContent = 'Gratis';
    } else {
      msgEl.classList.remove('is-unlocked');
      const remainingCents = threshold - subtotalCents;
      const remaining = (remainingCents / 100).toLocaleString('es-MX', {
        style: 'currency',
        currency: this.dataset.currency || 'MXN',
      });
      msgEl.appendChild(document.createTextNode('Te faltan '));
      const strong = document.createElement('strong');
      strong.textContent = remaining;
      msgEl.appendChild(strong);
      msgEl.appendChild(document.createTextNode(' para envío gratis'));
      if (fillEl) fillEl.style.width = `${Math.min(100, (subtotalCents / threshold) * 100)}%`;
      if (costEl) costEl.textContent = 'al checkout';
    }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-ma-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.setAttribute('data-ma-cart-count', String(count));
    });
  }

  updateSubtotal(totalPrice, currency) {
    const formatted = (totalPrice / 100).toLocaleString('es-MX', {
      style: 'currency',
      currency: currency || this.dataset.currency || 'MXN',
    });
    const subtotalEl = this.querySelector('[data-ma-cart-subtotal]');
    const totalEl = this.querySelector('[data-ma-cart-total]');
    if (subtotalEl) subtotalEl.textContent = formatted;
    if (totalEl) totalEl.textContent = formatted;
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.updateShipping(cart.total_price);
      this.updateCartCount(cart.item_count);
      this.updateSubtotal(cart.total_price, cart.currency);
    } catch (err) {
      console.error('Cart refresh failed', err);
    }
  }
}

customElements.define('ma-cart-drawer', MaCartDrawer);

window.addEventListener('popstate', () => {
  const drawer = document.querySelector('ma-cart-drawer');
  if (drawer && drawer.hasAttribute('open')) drawer.close(true);
});

// Aviso «Agregado» en celular (2-nov-2026). Al agregar, el cajón tapaba toda
// la pantalla y se sentía como «me manda al carrito y no puedo seguir
// agregando» (Zigpoll, 28-sep y 28-oct). En celular se avisa y se sigue
// comprando; el carrito se abre si lo tocan. En escritorio el cajón es
// lateral y no estorba: ahí se abre como siempre.
function avisoAgregado() {
  let t = document.querySelector('[data-ma-agregado]');
  if (!t) {
    t = document.createElement('div');
    t.className = 'ma-agregado';
    t.setAttribute('data-ma-agregado', '');
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    const txt = document.createElement('span');
    txt.className = 'ma-agregado__txt';
    txt.textContent = '✓ Agregado al carrito';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ma-agregado__ver';
    btn.textContent = 'Ver carrito';
    btn.addEventListener('click', () => { t.classList.remove('is-on'); window.MaCart.open(); });
    t.append(txt, btn);
    document.body.appendChild(t);
  }
  t.classList.remove('is-on');
  void t.offsetWidth;
  t.classList.add('is-on');
  clearTimeout(t._timer);
  t._timer = setTimeout(() => t.classList.remove('is-on'), 4000);
}

window.MaCart = {
  open: () => document.dispatchEvent(new CustomEvent('ma:cart:open')),
  // Para después de AGREGAR un producto; `open` queda para «ver carrito».
  agregado: () => {
    if (window.matchMedia('(max-width: 768px)').matches) avisoAgregado();
    else document.dispatchEvent(new CustomEvent('ma:cart:open'));
  },
  close: () => document.dispatchEvent(new CustomEvent('ma:cart:close')),
  refresh: () => {
    const drawer = document.querySelector('ma-cart-drawer');
    if (drawer) drawer.refresh();
  },
  // Re-render selectivo desde el HTML de la sección (preserva recomendaciones).
  // Lo usa component-card-grid.js al agregar desde una card o desde el "+".
  renderSection: (html, itemCount) => {
    const drawer = document.querySelector('ma-cart-drawer');
    if (drawer) drawer._renderSection(html, itemCount);
  },
};
